"use client";

import {
  ListGridViewButton,
  ViewMoreButton,
} from "@punchup/ui-common/components/common";
import { Separator } from "@punchup/ui-common/shadcn/ui/separator";
import { VenueEvents, trackEvent } from "@punchup/analytics";
import { cn } from "@punchup/utils/cn";
import { debounce } from "@punchup/utils/debounce";
import { useMediaQuery } from "@punchup/utils/hooks";
import { roundToFullRows, useGridRowCount } from "../hooks";
import { useEffect, useMemo, useRef, useState } from "react";
import { useMultipleWaitlistStatus } from "../hooks/useWaitlistStatus";
import { WaitlistSubscription } from "@punchup/data/client";
import {
  useVenuePage,
  useVenuePageGroupedShows,
  useVenuePageShows,
  useVenuePageTheme,
} from "@punchup/venues";

import Link from "next/link";
import React from "react";
import { ShowWithComedian } from "@punchup/data";
import ShowsNearbyEmptyShows from "./ShowsNearbyEmptyShows";
import TourDateRow from "./TourDateRow";
import TourDateRowThemed from "./TourDateRowThemed";
import { User } from "@supabase/supabase-js";
import { VenuePagesRow } from "@punchup/data";
import type { VenueShowGroup } from "../../data/venueGridItem";
import VenueShowGroupCard, {
  VenueShowGroupCardThemed,
} from "./VenueShowGroupCard";
import VenueShowsSearch from "./VenueShowsSearch";
import VenueShowsSearchEmpty from "./VenueShowsSearchEmpty";
import { VenuePageUrlBase, getBasePath } from "./navigation/utils";
import dayjs from "@punchup/utils/dayjs";
import {
  ShowCardSkeleton,
  TourDateRowSkeleton,
} from "./skeletons/VenuePageSkeletons";

const INITIAL_SHOWS_LIST = 5;
const INITIAL_SHOWS_GRID = 8;
const MIN_CARD_WIDTH = 327; // px
const GRID_GAP = 24; // gap-6 = 1.5rem = 24px (desktop)

type ViewMode = VenuePagesRow["default_show_view"];

interface TourDatesGridProps {
  venueSlug: string;
  urlBase: VenuePageUrlBase;
  useVenueTheme?: boolean;
  followedShows: Array<ShowWithComedian>;
  followedComedians: string[];
  user?: User;
  internalReferrer?: string | null;
  followedShowsEnabled?: boolean;
}

export default function VenueUpcomingShows({
  venueSlug,
  urlBase,
  useVenueTheme = false,
  followedShows,
  followedComedians,
  user,
  internalReferrer,
  followedShowsEnabled = false,
}: TourDatesGridProps) {
  const { venuePage } = useVenuePage({ slug: venueSlug });
  const { logoImageBackgroundColor } = useVenuePageTheme(venueSlug);
  const [searchQuery, setSearchQuery] = useState("");
  const [hasExpanded, setHasExpanded] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>(
    venuePage?.default_show_view ?? "list"
  );

  // When the venue opts into `grouped` mode, shows that belong to the same
  // show-group collapse into one card. Grouping is done server-side over the
  // whole upcoming set (one fetch, no offset paging) so a multi-night group
  // never splits across a "View More" boundary.
  const wantsGrouped = venuePage?.shows_grouping_mode === "grouped";

  const {
    items: groupedItems,
    filteredItems: groupedFilteredItems,
    isLoading: isLoadingGrouped,
    error: groupedError,
  } = useVenuePageGroupedShows({
    venuePageId: venuePage?.id ?? "",
    enabled: !!venuePage?.id && wantsGrouped,
    searchTerm: searchQuery,
  });

  // Fall back to the individual grid whenever the grouped endpoint is
  // unreachable so the shows section never renders blank. This covers the
  // real-world gaps: a `{slug}.punchup.live` micro-site fetches the shared
  // `punchup.live` API (not its own origin), so grouped mode depends on that
  // endpoint being live there; the same applies during a deploy window or any
  // transient failure. Grouped is a display nicety — the individual grid is the
  // safe baseline that always works.
  const isGrouped = wantsGrouped && !groupedError;

  const {
    shows: allShows,
    searchResults,
    isSearching,
    fetchMoreShows,
    isLoadingMore,
    hasMore,
  } = useVenuePageShows({
    venuePageId: venuePage?.id ?? "",
    venueSlug,
    enabled: !!venuePage?.id && !isGrouped,
    searchTerm: searchQuery,
  });

  // Ref for grid container to calculate items per row
  const gridContainerRef = useRef<HTMLDivElement>(null);
  const itemsPerRow = useGridRowCount(
    gridContainerRef,
    MIN_CARD_WIDTH,
    GRID_GAP
  );

  const venueLogoImg = venuePage ? venuePage.logo_img : null;

  // Track no results (debounced)
  const trackNoResults = useMemo(
    () =>
      debounce((query: string) => {
        trackEvent(VenueEvents.VENUE_SEARCH_NO_RESULTS, {
          search_query: query,
        });
      }, 800),
    []
  );

  // The single shows the grouped grid renders (group cards have no buy/waitlist
  // CTA), so they still participate in the batched waitlist lookup below.
  const groupedSingleShows = useMemo(
    () =>
      groupedItems
        .filter((item) => item.type === "single")
        .map((item) => (item.type === "single" ? item.show : null))
        .filter((s): s is ShowWithComedian => s !== null),
    [groupedItems]
  );

  // Batch-fetch waitlist status for every sold-out show that could be
  // displayed (followed / paginated / search / grouped-single) in one request.
  // Each row reads its subscription out of this single query, avoiding the
  // N-per-page /api/v2/user/waitlist fan-out that triggers rate limits.
  const soldOutShowsForWaitlist = useMemo(() => {
    const seen = new Set<string>();
    const out: Array<{ id: string; ownerId: string; isVenue: boolean }> = [];
    for (const s of [
      ...followedShows,
      ...allShows,
      ...searchResults,
      ...groupedSingleShows,
    ]) {
      if (!s.is_sold_out || seen.has(s.id)) continue;
      seen.add(s.id);
      out.push({ id: s.id, ownerId: s.venue_id, isVenue: true });
    }
    return out;
  }, [followedShows, allShows, searchResults, groupedSingleShows]);
  const { getSubscriptionForShow } = useMultipleWaitlistStatus(
    soldOutShowsForWaitlist
  );

  const sortShowByDatetime = (a: ShowWithComedian, b: ShowWithComedian) =>
    dayjs(a.datetime).valueOf() - dayjs(b.datetime).valueOf();

  const followedShowsSorted = [...followedShows].sort(sortShowByDatetime);
  const allShowsSorted = [...allShows].sort(sortShowByDatetime);

  const showsLimit =
    viewMode === "list" ? INITIAL_SHOWS_LIST : INITIAL_SHOWS_GRID;

  const initialDisplayCount = useMemo(() => {
    let count = showsLimit;
    if (viewMode === "grid" && itemsPerRow > 1) {
      count = roundToFullRows(count, itemsPerRow);
    }
    return count;
  }, [showsLimit, viewMode, itemsPerRow]);

  const allShowsSortedAndFiltered = useMemo(() => {
    // While searching, show server-side search results
    if (searchQuery) {
      return [...searchResults].sort(sortShowByDatetime);
    }

    // After expanding, show all loaded shows
    if (hasExpanded) {
      return allShowsSorted;
    }

    // Initial view: show limited count (rounded for grid)
    return allShowsSorted.slice(0, initialDisplayCount);
  }, [
    searchQuery,
    searchResults,
    hasExpanded,
    allShowsSorted,
    initialDisplayCount,
  ]);

  // Grouped mode: display-side slice over the fully-loaded, already-grouped list.
  const groupedDisplayItems = useMemo(() => {
    if (searchQuery || hasExpanded) return groupedFilteredItems;
    return groupedFilteredItems.slice(0, initialDisplayCount);
  }, [groupedFilteredItems, searchQuery, hasExpanded, initialDisplayCount]);

  const groupedHasMore =
    !searchQuery && groupedFilteredItems.length > groupedDisplayItems.length;

  // Track when search yields no results. Wait until the search has settled
  // (not debouncing, not fetching) so the empty skeleton phase doesn't
  // get tracked as a zero-result search.
  const filteredResultCount = isGrouped
    ? groupedFilteredItems.length
    : allShowsSortedAndFiltered.length;
  useEffect(() => {
    if (
      searchQuery &&
      !isSearching &&
      filteredResultCount === 0 &&
      !isLoadingMore
    ) {
      trackNoResults(searchQuery);
    }
  }, [
    filteredResultCount,
    searchQuery,
    isSearching,
    trackNoResults,
    isLoadingMore,
  ]);

  // Shows already loaded but hidden behind the initial display slice. A venue
  // with fewer upcoming shows than one server page (so `hasMore` is false)
  // still needs "View More" to reveal them.
  const hasUnshownCachedShows =
    !hasExpanded && allShowsSorted.length > initialDisplayCount;

  const handleViewMoreClick = async () => {
    if (isGrouped) {
      // Everything is already loaded; "View More" just widens the display slice.
      setHasExpanded(true);
      return;
    }
    setHasExpanded(true);
    if (!hasUnshownCachedShows) {
      await fetchMoreShows();
    }
  };

  const handleListClick = () => {
    setViewMode("list");
  };

  const handleGridClick = () => {
    setViewMode("grid");
  };

  // Only show search if there are any shows
  const hasShows = isGrouped ? groupedItems.length > 0 : allShows.length > 0;
  const hasFollowedShows = followedShowsSorted.length > 0;
  const isInitialLoading = isGrouped && isLoadingGrouped && !hasShows;
  const showMoreButton = isGrouped
    ? groupedHasMore
    : hasMore || hasUnshownCachedShows;

  const subtitleClassName =
    "font-light text-base text-secondary-blue font-sans normal-case";

  const gridContainerClassName = cn(
    viewMode === "list" && "flex flex-col gap-y-3",
    viewMode === "grid" && "grid gap-4 md:gap-6"
  );
  const gridContainerStyle =
    viewMode === "grid"
      ? {
          gridTemplateColumns: `repeat(auto-fill, minmax(${MIN_CARD_WIDTH}px, 1fr))`,
        }
      : undefined;

  const sharedItemProps = {
    viewMode,
    user,
    followedComedians,
    internalReferrer,
    useVenueTheme,
    venueSlug,
    venueLogoImg,
    logoImageBackgroundColor,
  };

  const renderSkeletons = () =>
    Array.from({ length: viewMode === "grid" ? 8 : 5 }).map((_, i) =>
      viewMode === "grid" ? (
        <ShowCardSkeleton key={`skeleton-${i}`} />
      ) : (
        <TourDateRowSkeleton key={`skeleton-${i}`} />
      )
    );

  const renderAllShows = () => {
    if (isGrouped) {
      return groupedDisplayItems.map((item) =>
        item.type === "group" ? (
          <GroupView
            key={`group-${item.group.key}`}
            group={item.group}
            {...sharedItemProps}
          />
        ) : (
          <ShowView
            key={item.show.id}
            show={item.show}
            {...sharedItemProps}
            waitlistSubscription={getSubscriptionForShow(item.show.id)}
          />
        )
      );
    }

    if (searchQuery && isSearching) {
      return renderSkeletons();
    }

    return allShowsSortedAndFiltered.map((show) => (
      <ShowView
        key={show.id}
        show={show}
        {...sharedItemProps}
        waitlistSubscription={getSubscriptionForShow(show.id)}
      />
    ));
  };

  return (
    <div className="flex flex-col gap-6">
      <section className="flex flex-col gap-y-6 pt-2 md:pt-6">
        <div className="flex flex-col md:flex-row justify-between gap-y-3">
          <div className="flex flex-col gap-y-2">
            <h2 className="text-2xl font-bold text-primary-blue">
              Upcoming shows
            </h2>
            <div className="hidden md:block">
              {searchQuery ? (
                // placeholder div to maintain layout
                <div className="h-6" />
              ) : hasFollowedShows && followedShowsEnabled ? (
                <h3 className={cn(subtitleClassName)}>
                  Comedians you're subscribed to:
                </h3>
              ) : hasShows ? (
                <h3 className={cn(subtitleClassName)}>All shows:</h3>
              ) : null}
            </div>
          </div>
          {hasShows && (
            <div className="flex flex-row items-center justify-between md:gap-x-4">
              <VenueShowsSearch
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
              />
              <ListGridViewButton
                viewMode={viewMode}
                onListClick={handleListClick}
                onGridClick={handleGridClick}
                className="hidden md:flex"
              />
            </div>
          )}
        </div>
        {/* render something if there's at least 1 show */}
        {hasShows || hasFollowedShows ? (
          <div className="flex flex-col gap-y-4 md:gap-y-6">
            {hasFollowedShows && followedShowsEnabled && (
              <>
                <h3 className={cn(subtitleClassName, "md:hidden")}>
                  Comedians you're subscribed to:
                </h3>
                <div className="flex flex-col pb-2 gap-y-3">
                  <div
                    className={gridContainerClassName}
                    style={gridContainerStyle}
                  >
                    {/* render only the shows from the user's followed comedians */}
                    {followedShowsSorted.map((show) => (
                      <ShowView
                        key={show.id}
                        show={show}
                        {...sharedItemProps}
                        waitlistSubscription={getSubscriptionForShow(show.id)}
                      />
                    ))}
                  </div>
                </div>
              </>
            )}
            <div className="flex flex-col gap-y-4 md:gap-y-6">
              {/* render the "All shows" title if followed shows feature is enabled and there are followed shows */}
              {!searchQuery && hasShows && (
                <h3
                  className={cn(
                    subtitleClassName,
                    hasFollowedShows && followedShowsEnabled ? "" : "md:hidden"
                  )}
                >
                  All shows:
                </h3>
              )}
              {searchQuery && !isSearching && filteredResultCount === 0 ? (
                <VenueShowsSearchEmpty
                  query={searchQuery}
                  onClear={() => setSearchQuery("")}
                />
              ) : (
                <div
                  ref={gridContainerRef}
                  className={gridContainerClassName}
                  style={gridContainerStyle}
                >
                  {renderAllShows()}
                </div>
              )}
            </div>
          </div>
        ) : isInitialLoading ? (
          <div className={gridContainerClassName} style={gridContainerStyle}>
            {renderSkeletons()}
          </div>
        ) : (
          <>
            {searchQuery ? (
              <ShowsNearbyEmptyShows className="pt-4" />
            ) : (
              <div className="flex flex-col items-start justify-center h-full pt-4 gap-3 text-secondary-blue">
                <p>No shows scheduled yet.</p>
                <p>New shows are added all the time, so check back soon!</p>
              </div>
            )}
          </>
        )}

        {!searchQuery && showMoreButton && (
          <div className="flex flex-row justify-center items-center w-full">
            <ViewMoreButton
              onClick={handleViewMoreClick}
              disabled={isGrouped ? false : isLoadingMore}
            />
          </div>
        )}

        {!searchQuery && hasShows && !showMoreButton && (
          <div className="flex flex-row justify-center items-center w-full">
            <Link
              href={`${getBasePath(urlBase, venueSlug)}/calendar`}
              className="px-5 py-3 rounded-md text-base font-semibold text-primary-blue transition-colors ease-in-out duration-200 hover:bg-neutral-100"
            >
              See all shows on the calendar
            </Link>
          </div>
        )}
      </section>
    </div>
  );
}

interface SharedItemProps {
  viewMode: ViewMode;
  user?: User;
  followedComedians: string[];
  internalReferrer: string | null | undefined;
  useVenueTheme: boolean;
  venueSlug: string;
  venueLogoImg: string | null;
  logoImageBackgroundColor: string;
}

function ShowView({
  show,
  viewMode,
  user,
  followedComedians,
  internalReferrer,
  useVenueTheme,
  venueSlug,
  venueLogoImg,
  logoImageBackgroundColor,
  waitlistSubscription,
}: SharedItemProps & {
  show: ShowWithComedian;
  waitlistSubscription: WaitlistSubscription | null;
}) {
  const isDesktop = useMediaQuery("(min-width: 768px)");

  return (
    <React.Fragment key={show.id}>
      <div className={cn(viewMode === "grid" && "w-full aspect-[4/5]")}>
        {useVenueTheme && venueSlug ? (
          <TourDateRowThemed
            venueSlug={venueSlug}
            show={show}
            comedians={show.show_comedians}
            user={user}
            internalReferrer={internalReferrer || undefined}
            followedComedians={followedComedians}
            mobileView={viewMode === "grid"}
            venueLogoImg={venueLogoImg}
            cardBackgroundColor={logoImageBackgroundColor}
            waitlistSubscription={waitlistSubscription}
          />
        ) : (
          <TourDateRow
            show={show}
            comedians={show.show_comedians}
            user={user}
            internalReferrer={internalReferrer || undefined}
            followedComedians={followedComedians}
            mobileView={viewMode === "grid"}
            venueLogoImg={venueLogoImg}
            cardBackgroundColor={logoImageBackgroundColor}
            venueSlug={venueSlug}
            waitlistSubscription={waitlistSubscription}
          />
        )}
      </div>
      {/* only display the separator on list view and desktop */}
      {isDesktop && viewMode === "list" && (
        <Separator className="border-neutral-200" />
      )}
    </React.Fragment>
  );
}

function GroupView({
  group,
  viewMode,
  followedComedians,
  useVenueTheme,
  venueSlug,
  venueLogoImg,
  logoImageBackgroundColor,
}: SharedItemProps & {
  group: VenueShowGroup;
}) {
  const isDesktop = useMediaQuery("(min-width: 768px)");

  return (
    <React.Fragment key={group.key}>
      <div className={cn(viewMode === "grid" && "w-full aspect-[4/5]")}>
        {useVenueTheme && venueSlug ? (
          <VenueShowGroupCardThemed
            venueSlug={venueSlug}
            group={group}
            mobileView={viewMode === "grid"}
            venueLogoImg={venueLogoImg}
            followedComedians={followedComedians}
          />
        ) : (
          <VenueShowGroupCard
            venueSlug={venueSlug}
            group={group}
            mobileView={viewMode === "grid"}
            venueLogoImg={venueLogoImg}
            followedComedians={followedComedians}
            cardBackgroundColor={logoImageBackgroundColor}
          />
        )}
      </div>
      {isDesktop && viewMode === "list" && (
        <Separator className="border-neutral-200" />
      )}
    </React.Fragment>
  );
}
